import _ from "underscore";

import * as Pivot from "cljs/metabase.pivot.js";
import { formatValue } from "metabase/visualizations/lib/formatting";
import { makeCellBackgroundGetter } from "metabase/visualizations/lib/table_format";
import { migratePivotColumnSplitSetting } from "metabase-lib/v1/queries/utils/pivot";

import { COLUMN_SPLIT_SETTING, MEASURES_AS_ROWS_SETTING } from "./data_grid.js";

export function isPivotGroupColumn(col) {
  return col.name === "pivot-grouping";
}

export function parseKey(key) {
  try {
    return JSON.parse(key);
  } catch (e) {
    if (typeof key === "string") {
      const parts = key.slice(1, -1).match(/('[^']*'|[^ ]+)/g);
      if (parts) {
        return parts.map((part) => {
          if (part.startsWith("'")) {
            return part.slice(1, -1);
          }
          const number = parseFloat(part);
          if (!isNaN(number) && isFinite(number)) {
            return number;
          }
          return part;
        });
      }
    }
    throw e;
  }
}

export function formatKey(key) {
  return JSON.stringify(key);
}

function makeFormatters(indexes, columnSettings) {
  return indexes.map((index) =>
    _.memoize(
      (value) => formatValue(value, columnSettings[index]),
      (value) => [value, index].join(),
    ),
  );
}

export function multiLevelPivotWithRowSorting(
  data,
  settings,
  rowSortOrder = {},
) {
  if (!settings[COLUMN_SPLIT_SETTING]) {
    return null;
  }
  const columnSplit = migratePivotColumnSplitSetting(
    settings[COLUMN_SPLIT_SETTING] ?? { rows: [], columns: [], values: [] },
    data.cols,
  );
  const columnsWithoutPivotGroup = Pivot.columns_without_pivot_group(data.cols);

  const {
    columns: columnColumnIndexes,
    rows: rowColumnIndexes,
    values: valueColumnIndexes,
  } = _.mapObject(columnSplit, (columnNames) =>
    columnNames
      .map((columnName) =>
        columnsWithoutPivotGroup.findIndex((col) => col.name === columnName),
      )
      .filter((index) => index !== -1),
  );

  const columnSettings = columnsWithoutPivotGroup.map((column) =>
    settings.column(column),
  );

  const topIndexFormatters = makeFormatters(
    columnColumnIndexes,
    columnSettings,
  );
  const leftIndexFormatters = makeFormatters(rowColumnIndexes, columnSettings);
  const valueFormatters = makeFormatters(valueColumnIndexes, columnSettings);

  const makeColorGetter = (primaryRows) =>
    makeCellBackgroundGetter(
      primaryRows,
      columnsWithoutPivotGroup,
      settings["table.column_formatting"] ?? [],
      true,
    );

  const result = Pivot.process_pivot_table_with_row_sorting(
    data,
    rowColumnIndexes,
    columnColumnIndexes,
    valueColumnIndexes,
    columnsWithoutPivotGroup,
    topIndexFormatters,
    leftIndexFormatters,
    valueFormatters,
    settings,
    columnSettings,
    makeColorGetter,
    rowSortOrder,
    settings[MEASURES_AS_ROWS_SETTING] ?? false,
  );

  return {
    ...result,
    rowCount: result.rowIndex.length,
    columnCount: result.columnIndex.length,
    rowIndexes: rowColumnIndexes,
    columnIndexes: columnColumnIndexes,
    valueIndexes: valueColumnIndexes,
    rowMetrics: settings[MEASURES_AS_ROWS_SETTING],
    columnsWithoutPivotGroup,
  };
}
